// @flow
import {type SagaIterator} from 'redux-saga';
import {FULFILLED, PENDING} from 'redux-promise-middleware';

import type {ActionType} from 'constants/flow/flowTypes';
import type {PageAppType} from 'reducers/app/pageApp/pageAppType';

import {CUSTOMER, APP} from 'constants/actions';
import {CHECKOUT_PAYMENT_TYPES, CHECKOUT_STEPS} from 'constants/checkout';
import {put, putResolve, takeEvery, call, select, cancel, take} from 'redux-saga/effects';
import {
    fetchCustomerAddresses,
    setCurrentShippingAddress,
    setCurrentBillingAddress,
    setCurrentStep,
    fetchAddressesUnauthorizedCustomer,
    getPaymentMethods as getPaymentMethodsAction,
} from 'pages/checkout/store/actions/checkout';
import {fetchRegions} from 'actions/location';
import {
    getAddressesList,
    getCurrentBillingAddress,
    getCurrentShippingAddress,
    isCheckoutPage,
    getCheckoutType,
} from 'selectors/checkout/checkout';
import {getCustomer} from 'selectors/customer/customer';
import {getCurrentPage} from 'selectors/app/app';

/**
 * Returns saved address after first step on checkout if customer decided to reload checkout page
 */
export function* getUnAuthCustomerAddress(): SagaIterator {
    const unAuthAddress = yield call(fetchAddressesUnauthorizedCustomer);
    yield putResolve(unAuthAddress);
    const currentShippingAddress = yield select(getCurrentShippingAddress);
    const currentBillingAddress = yield select(getCurrentBillingAddress);
    const currentShippingRegionsList = yield call(
        fetchRegions,
        currentShippingAddress.data.country,
    );
    yield putResolve(currentShippingRegionsList);
    const currentBillingRegionsList = yield call(fetchRegions, currentBillingAddress.data.country);
    yield putResolve(currentBillingRegionsList);
}

/**
 * Returns customer addresses if customer logged
 */
export function* getAuthCustomerAddress(): SagaIterator {
    const action = yield call(fetchCustomerAddresses);
    yield putResolve(action);
    const addresses = yield select(getAddressesList);
    if (addresses.length > 0) {
        const currentShipping = yield call(setCurrentShippingAddress, addresses[0], 0);
        yield put(currentShipping);
        const currentBilling = yield call(setCurrentBillingAddress, addresses[0], 0);
        yield put(currentBilling);
    }
}

/**
 * Logout with cart items saving
 */
export function* getCustomerAddresses(): SagaIterator {
    const customer = yield select(getCustomer);
    const isCheckout = yield select(isCheckoutPage);
    if (customer.is_logged_in) {
        yield call(getAuthCustomerAddress);
    } else if (isCheckout) {
        yield call(getUnAuthCustomerAddress);
    }
}

/**
 * Reset active step if we move to checkout page
 * @param {ActionType<PageAppType>} action after change page
 */
export function* resetStep(action: ActionType<PageAppType>): SagaIterator {
    if (action.payload.page === 'checkout') {
        yield put(setCurrentStep(CHECKOUT_STEPS.SHIPPING));
    }
}

/**
 * Check if user log in/out, and reset step on default if it is true
 */
export function* resetStepIfLogged(): SagaIterator {
    const location = yield select(getCurrentPage);
    if (location.page !== 'checkout') yield cancel();
    const {is_logged_in: isLoggedPrev} = yield select(getCustomer);
    yield take(`${CUSTOMER.GET_CUSTOMER_DATA}_${FULFILLED}`);
    const {is_logged_in: isLoggedCurrent} = yield select(getCustomer);
    if (isLoggedPrev !== isLoggedCurrent) {
        yield put(setCurrentStep(CHECKOUT_STEPS.SHIPPING));
    }
}

/**
 * handle payment methods
 */
export function* handlePaymentMethods(): SagaIterator {
    const isCheckout = yield select(isCheckoutPage);
    const checkoutType = yield select(getCheckoutType);
    const paymentMethodName = CHECKOUT_PAYMENT_TYPES[checkoutType];
    if (isCheckout && paymentMethodName) yield put(getPaymentMethodsAction());
}

/**
 * Checkout root saga
 */
export default function* checkoutSaga(): Generator<void, void, string> {
    yield takeEvery([`${CUSTOMER.GET_CUSTOMER_DATA}_${FULFILLED}`], getCustomerAddresses);
    yield takeEvery(`${CUSTOMER.GET_CUSTOMER_DATA}_${PENDING}`, resetStepIfLogged);
    yield takeEvery(APP.SAVE_LOCATION, resetStep);
    yield takeEvery(APP.SAVE_LOCATION, handlePaymentMethods);
}
